import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TextField } from '@material-ui/core';
import { Check, Cancel } from '@material-ui/icons';
import { IconButton} from '@coolremote/shared';
import useStyles from './styles';
/**
 * EditInPlaceInput component that displays an input field with save and reset buttons.
 * Buttons are only visible when the input field has been edited.
 *
 * @param {Object} props - Component properties.
 * @param {string} props.value - The initial value of the input field.
 * @param {Function} props.onSave - Callback function to handle saving the new value.
 * @returns {JSX.Element} The rendered EditInPlaceInput component.
 */
const EditInPlaceInput = ({ value: initialValue, onSave }) => {
  const classes = useStyles();
  const [value, setValue] = useState(initialValue);
  const [isEdited, setIsEdited] = useState(false);

  // Update local state if the `initialValue` prop changes
  useEffect(() => {
    setValue(initialValue);
    setIsEdited(false); // disable buttons after saving
  }, [initialValue]);

  /**
   * Handles input changes and updates the edited state.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event - The input change event.
   */
  const handleInputChange = (event) => {
    setValue(event.target.value);
    setIsEdited(event.target.value !== initialValue); // enable buttons if edited
  };

  /**
   * Saves the current value by calling the onSave callback.
   */
  const handleSave = () => {
    onSave(value);
  };

  /**
   * Resets the input field to its initial value.
   */
  const handleReset = () => {
    setValue(initialValue);
    setIsEdited(false); // disable buttons
  };

  return (
    <div className={classes.editInPlace}>
      <TextField
        value={value}
        onChange={handleInputChange}
        variant="outlined"
        size="small"
      />
      {isEdited && (
        <div className={classes.actions}>
          <IconButton color="primary" onClick={handleSave}>
            <Check />
          </IconButton>
          <IconButton color="secondary" onClick={handleReset}>
            <Cancel />
          </IconButton>
        </div>
      )}
    </div>
  );
};

EditInPlaceInput.propTypes = {
  /** The initial value of the input field. */
  value: PropTypes.string,

  /** Callback function to handle saving the new value. */
  onSave: PropTypes.func,
};

EditInPlaceInput.defaultProps = {
  value: '', // Default value as an empty string
  onSave: () => {}, // Default onSave as a no-op function
};

export default EditInPlaceInput;
